import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, Layers, CheckCircle2, Trophy, 
  Calculator, Zap, Volume2, Sparkles, Search, 
  ArrowRight, Brain, Clock, ShieldAlert, Award
} from 'lucide-react';
import { useAuth } from './AuthBridge';
import { SuggestionService, SuggestionItem } from '../services/suggestionService';
import { useThemeTokens } from '../hooks/useActiveTheme';

interface AdvanceSuggestionPanelProps {
  source: 'guide' | 'quiz' | 'exam' | 'simulator' | 'skills';
  score?: number;
  moduleId?: string;
  moduleTitle?: string;
  className?: string;
}

export const AdvanceSuggestionPanel: React.FC<AdvanceSuggestionPanelProps> = ({
  source,
  score,
  moduleId,
  moduleTitle,
  className = ''
}) => {
  const { user, activeCert } = useAuth();
  const navigate = useNavigate();
  const tokens = useThemeTokens();

  const engineBadge = React.useMemo(() => {
    const missed = user?.missedQuestions?.length || 0;
    const hours = user?.stats?.studyHours || 0;
    const isPro = user?.planTier === 'paid';
    
    if (missed > 5) {
      return {
        text: 'Targeted Error Remediation Pipeline Calibrated',
        color: 'text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-900 bg-rose-50/60 dark:bg-rose-950/20'
      };
    }
    if (hours > 4) {
      return {
        text: isPro ? 'Elite Adaptive Performance Diagnostics Operational' : 'Dynamic Pathway Syllabus Optimizer Calibrated',
        color: 'text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20'
      };
    }
    return {
      text: 'Precision Board Readiness Catalyst Active',
      color: 'text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-900 bg-indigo-50/60 dark:bg-indigo-950/20'
    };
  }, [user]);

  const suggestions = React.useMemo(() => {
    return SuggestionService.generateSuggestions(user, activeCert, {
      source,
      score,
      moduleId,
      moduleTitle
    });
  }, [user, activeCert, source, score, moduleId, moduleTitle]);

  const getIcon = (type: SuggestionItem['type']) => {
    switch (type) {
      case 'guide': return BookOpen;
      case 'skills': return Layers;
      case 'quiz': return CheckCircle2;
      case 'exam': return Trophy;
      case 'simulator': return Calculator;
      case 'flashcards': return Zap;
      case 'podcasts': return Volume2;
      case 'tutor': return Sparkles;
      case 'search': return Search;
      default: return Brain;
    }
  };

  const getThemeColor = (type: SuggestionItem['type']) => {
    switch (type) {
      case 'guide': 
        return {
          iconBg: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-650 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900',
          hoverBorder: 'hover:border-indigo-405 hover:bg-indigo-50/10'
        };
      case 'skills':
        return {
          iconBg: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900',
          hoverBorder: 'hover:border-emerald-405 hover:bg-emerald-50/10'
        };
      case 'quiz':
        return {
          iconBg: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900',
          hoverBorder: 'hover:border-indigo-405 hover:bg-indigo-50/10'
        };
      case 'exam':
        return {
          iconBg: 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700',
          hoverBorder: 'hover:border-slate-505 hover:bg-slate-100/10'
        };
      case 'simulator':
        return {
          iconBg: 'bg-cyan-50 dark:bg-cyan-950/40 text-cyan-600 dark:text-cyan-400 border border-cyan-100 dark:border-cyan-900',
          hoverBorder: 'hover:border-cyan-405 hover:bg-cyan-50/10'
        };
      case 'flashcards':
        return {
          iconBg: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-900',
          hoverBorder: 'hover:border-amber-405 hover:bg-amber-50/10'
        };
      case 'podcasts':
        return {
          iconBg: 'bg-violet-50 dark:bg-violet-950/40 text-violet-600 dark:text-violet-400 border border-violet-100 dark:border-violet-900',
          hoverBorder: 'hover:border-violet-405 hover:bg-violet-50/10'
        };
      case 'tutor':
        return {
          iconBg: 'bg-fuchsia-50 dark:bg-fuchsia-950/40 text-fuchsia-600 dark:text-fuchsia-400 border border-fuchsia-100 dark:border-fuchsia-900',
          hoverBorder: 'hover:border-fuchsia-405 hover:bg-fuchsia-50/10'
        };
      case 'search':
        return {
          iconBg: 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border border-rose-100 dark:border-rose-900',
          hoverBorder: 'hover:border-rose-405 hover:bg-rose-50/10'
        };
      default:
        return {
          iconBg: 'bg-slate-50 dark:bg-slate-950/40 text-slate-600 dark:text-slate-400 border border-slate-100 dark:border-slate-900',
          hoverBorder: 'hover:border-slate-405 hover:bg-slate-50/10'
        };
    }
  };

  const handleAction = (url: string) => {
    navigate(url);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (suggestions.length === 0) return null;

  return (
    <div id="advance-suggestions-panel" className={`space-y-6 max-w-4xl mx-auto ${className}`}>
      {/* Dynamic Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
        <div className="flex items-center gap-2.5">
          <div className={`p-2 ${tokens.primaryBtn} text-white rounded-xl shadow-md`}>
            <Brain size={18} className="animate-pulse" />
          </div>
          <div>
            <h3 className="text-sm font-black uppercase text-slate-900 dark:text-white tracking-[0.2em]">Next Best Practice Advisory</h3>
            <p className="text-[10px] sm:text-xs text-slate-400 font-bold max-w-md">Clinically formulated curriculum mapping based on your active scores and syllabus gaps.</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 self-start sm:self-center font-black text-[9px] tracking-widest px-3 py-1.5 rounded-full border uppercase shadow-sm transition-all duration-300 ${engineBadge.color}`}>
          <Clock size={12} className="animate-pulse shrink-0" /> {engineBadge.text}
        </div>
      </div>

      {/* Suggestion Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {suggestions.map((item) => {
          const IconComponent = getIcon(item.type);
          const theme = getThemeColor(item.type);

          return (
            <div 
              key={item.id} 
              className={`flex flex-col justify-between p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800/80 rounded-[2rem] shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 ${theme.hoverBorder} group relative overflow-hidden`}
            >
              {/* Top ambient glow */}
              <div className="absolute top-0 right-0 w-28 h-28 bg-indigo-500/5 dark:bg-indigo-400/5 rounded-full blur-2xl pointer-events-none"></div>

              <div>
                {/* Header Row */}
                <div className="flex justify-between items-start mb-5">
                  <div className={`p-3 rounded-xl shrink-0 ${theme.iconBg} transition-transform group-hover:scale-105 duration-300`}>
                    <IconComponent size={20} />
                  </div>
                  <div className="flex gap-2">
                    <span className={`text-[8.5px] font-black uppercase tracking-wider border px-2.5 py-1 rounded-full ${item.badgeColor}`}>
                      {item.badge}
                    </span>
                    <span className="text-[8.5px] font-black uppercase bg-slate-50 dark:bg-slate-950 text-slate-500 border dark:border-slate-850 px-2.5 py-1 rounded-full flex items-center gap-1">
                      {item.timeEstimate}
                    </span>
                  </div>
                </div>

                {/* Core Text content */}
                <h4 className="text-lg font-black text-slate-900 dark:text-white leading-tight mb-2 uppercase tracking-tight">
                  {item.title}
                </h4>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium leading-relaxed mb-4">
                  {item.description}
                </p>

                {/* Clinical Judgement Rationale Box */}
                <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-dashed border-slate-150 dark:border-slate-800">
                  <div className={`flex items-center gap-1.5 mb-1 text-[8px] font-black ${tokens.accentText} uppercase tracking-widest`}>
                    <Award size={11} /> Clinical Rationale
                  </div>
                  <p className="text-[10px] text-slate-600 dark:text-slate-400 font-bold leading-relaxed italic">
                    "{item.rationale}"
                  </p>
                </div>
              </div>

              {/* Action Button CTA */}
              <button 
                onClick={() => handleAction(item.targetUrl)}
                className={`w-full py-3.5 ${tokens.primaryBtn} text-white rounded-xl font-black text-[10px] uppercase tracking-widest transition-all duration-300 shadow-md hover:shadow-xl hover:translate-y-px flex items-center justify-center gap-2`}
              >
                {item.actionText} 
                <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                <span className="text-[8px] font-normal text-slate-400 group-hover:text-white transition-colors">
                  (+{item.engagementXP} XP)
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
